/**
 * GalleryStorage — salva uma cópia da foto em local visível ao usuário fora
 * do app (Galeria do telefone / Files app).
 *
 * Estratégia Android:
 *  - Usa `@capacitor/filesystem` com `Directory.External` → grava em
 *    `/storage/emulated/0/Android/data/com.infrawork.mobile/files/Pictures/InfraWork/`
 *  - Em Android 11+ (scoped storage) é o melhor lugar acessível sem permissão
 *    extra: file manager (Files app) lista; alguns Galleries scanam.
 *  - Pra forçar aparição na Galeria, seria necessário um plugin custom usando
 *    MediaStore API. MVP: arquivo fica acessível via Files app garantidamente.
 *
 * Estratégia Web: no-op (PWA não tem conceito de galeria nativa).
 */

import { isNative } from '@/core/platform/runtime'

const GALLERY_DIR = 'Pictures/InfraWork'

interface FilesystemPlugin {
  writeFile(opts: {
    path: string
    data: string
    directory: string
    recursive?: boolean
  }): Promise<{ uri: string }>
}

let cachedPlugin: FilesystemPlugin | null = null
let cachedExternalDir: string | null = null
let unavailable = false

async function loadPlugin(): Promise<{ plugin: FilesystemPlugin; dir: string } | null> {
  if (unavailable) return null
  if (cachedPlugin && cachedExternalDir) {
    return { plugin: cachedPlugin, dir: cachedExternalDir }
  }
  try {
    const mod = (await import('@capacitor/filesystem')) as unknown as {
      Filesystem: FilesystemPlugin
      Directory: { External: string; ExternalStorage?: string }
    }
    cachedPlugin = mod.Filesystem
    // ExternalStorage existe em algumas versões (legacy); fallback pra External.
    cachedExternalDir = mod.Directory.ExternalStorage ?? mod.Directory.External
    return { plugin: cachedPlugin, dir: cachedExternalDir }
  } catch (e) {
    console.warn('[galleryStorage] plugin @capacitor/filesystem indisponível', e)
    unavailable = true
    return null
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => {
      const dataUrl = r.result as string
      const idx = dataUrl.indexOf(',')
      resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl)
    }
    r.onerror = () => reject(r.error)
    r.readAsDataURL(blob)
  })
}

/**
 * Salva uma cópia da foto na pasta Pictures/InfraWork do dispositivo.
 * NUNCA falha o fluxo principal — captura erros e loga.
 * Retorna o URI gerado (ou null se no-op/erro).
 *
 * Naming: `<projeto>-<YYYYMMDD>-<id>.jpg`
 *   ex.: `6.497-20260526-019d8a3b...jpg`
 * Facilita o usuário achar fotos por dia/projeto na Galeria.
 */
export async function saveToGallery(
  blob: Blob,
  options: {
    fotoId: string
    projetoCodigo: string | null
    capturedAt: string // ISO
  },
): Promise<string | null> {
  if (!isNative()) return null
  const loaded = await loadPlugin()
  if (!loaded) return null

  try {
    const date = options.capturedAt.slice(0, 10).replace(/-/g, '')
    const safeProj = (options.projetoCodigo ?? 'sem-projeto').replace(/[^\w.-]/g, '_')
    const filename = `${safeProj}-${date}-${options.fotoId.slice(0, 8)}.jpg`
    const path = `${GALLERY_DIR}/${filename}`
    const base64 = await blobToBase64(blob)
    const { uri } = await loaded.plugin.writeFile({
      path,
      data: base64,
      directory: loaded.dir,
      recursive: true,
    })
    return uri
  } catch (e) {
    console.warn('[galleryStorage.saveToGallery] falhou', e)
    return null
  }
}
